import axios from "axios";
import baseUrlNew from "../BaseUrl/index";

/**
 * Numeric product id for read APIs (getselected_inspection, etc.).
 * inspection_cars returns Django-signed car_id tokens (base64:timestamp:sig);
 * those must be decoded here — passing the token to getselected_inspection 500s.
 */
export function toProductId(value) {
  if (value == null || value === "") return null;
  const raw = String(value).trim();
  if (!raw) return null;

  if (/^\d+$/.test(raw)) return Number(raw);

  // signing.dumps payload: "<base64json>:<timestamp>:<signature>"
  if (raw.includes(":")) {
    try {
      const payload = raw.split(":")[0];
      let b64 = payload.startsWith(".") ? payload.slice(1) : payload;
      b64 = b64.replace(/-/g, "+").replace(/_/g, "/");
      const pad = "=".repeat((4 - (b64.length % 4)) % 4);
      const decoded = JSON.parse(atob(b64 + pad));
      const n = Number(decoded);
      if (Number.isFinite(n)) return n;
      if (decoded != null && decoded !== "") return decoded;
    } catch {
      /* fall through */
    }
  }

  const asNumber = Number(raw);
  return Number.isFinite(asNumber) ? asNumber : value;
}

/** Signed car_id for create_selected_options — must not be coerced to a number. */
export function toSignedCarId(value) {
  if (value == null || value === "") return null;
  return value;
}

export function mergeAnswerMaps(...maps) {
  const out = {};
  maps.forEach((map) => {
    if (!map || typeof map !== "object") return;
    Object.keys(map).forEach((key) => {
      const next = Array.isArray(map[key]) ? map[key] : [];
      const prev = Array.isArray(out[key]) ? out[key] : [];
      if (next.length >= prev.length) {
        out[key] = next;
      }
    });
  });
  return out;
}

function choiceLabel(selected) {
  if (!selected) return "";
  if (typeof selected === "string") return selected;
  return (
    selected.choice_select_ ||
    selected.choice_select ||
    selected.choice ||
    selected.answer ||
    ""
  );
}

function answersFromQuestions(questions) {
  if (!Array.isArray(questions)) return [];
  const answers = [];
  questions.forEach((q) => {
    const selected =
      q?.selected_inspection_question?.[0] ||
      q?.selected_inspection_question ||
      q?.selected ||
      q?.answer;
    const label = choiceLabel(
      Array.isArray(selected) ? selected[0] : selected
    );
    const questionText = q?.question || q?.title || "";
    if (label) {
      answers.push(questionText ? `${questionText}: ${label}` : String(label));
    }
  });
  return answers;
}

/**
 * Normalize inspection_cars / inspection_product `report` (and similar)
 * into { [sectionId|sectionTitle]: string[] }.
 */
export function answersMapFromReport(report) {
  if (report == null || report === "") return {};

  let payload = report;
  if (typeof payload === "string") {
    try {
      payload = JSON.parse(payload);
    } catch {
      return {};
    }
  }

  let sections = [];
  if (Array.isArray(payload)) {
    sections = payload;
  } else if (payload && typeof payload === "object") {
    if (Array.isArray(payload.data?.data)) sections = payload.data.data;
    else if (Array.isArray(payload.data)) sections = payload.data;
    else if (Array.isArray(payload.inspection_title)) sections = [payload];
    else if (Array.isArray(payload.report)) sections = payload.report;
  }

  const map = {};
  sections.forEach((section) => {
    if (!section || typeof section !== "object") return;
    const questions =
      section.inspection_title ||
      section.questions ||
      section.question_list ||
      (Array.isArray(section.selected_inspection_question) ? [section] : null);

    const answers = answersFromQuestions(questions);
    if (!answers.length && section.choice_select_) {
      answers.push(String(section.choice_select_));
    }
    if (!answers.length) return;

    if (section.id != null) map[section.id] = answers;
    if (section.title) map[section.title] = answers;
  });
  return map;
}

export function answersMapFromInspectionCar(car) {
  if (!car || typeof car !== "object") return {};

  // Preferred known fields on the inspection_cars row.
  const candidates = [
    car.report,
    car.selected_inspection,
    car.inspection,
    car.inspections,
    car.checklist,
    car.questions,
    car.answered_questions,
    car.question_answered,
    car.inspection_data,
    car.data,
  ];

  for (const candidate of candidates) {
    const map = answersMapFromReport(candidate);
    if (Object.keys(map).length) return map;
  }

  // Deep scan: any nested array that looks like checklist sections.
  const queue = [car];
  const seen = new Set();
  while (queue.length) {
    const node = queue.shift();
    if (!node || typeof node !== "object") continue;
    if (seen.has(node)) continue;
    seen.add(node);

    if (Array.isArray(node)) {
      const map = answersMapFromReport(node);
      if (Object.keys(map).length) return map;
      node.forEach((item) => queue.push(item));
      continue;
    }

    Object.keys(node).forEach((key) => {
      const value = node[key];
      if (value && typeof value === "object") queue.push(value);
    });
  }

  return {};
}

export function parseSelectedInspectionPayload(payload) {
  return answersMapFromReport(payload);
}

export function sectionAnswers(selectedBySection, item) {
  if (!selectedBySection || !item) return [];
  return (
    selectedBySection[item.id] ||
    selectedBySection[String(item.id)] ||
    selectedBySection[item.title] ||
    []
  );
}

/**
 * Load the current vehicle row from inspection_cars (same source as dashboard).
 */
export async function fetchInspectionCarRow(carId) {
  const productId = toProductId(carId);
  if (productId == null && carId == null) return null;

  const user =
    localStorage.getItem("INSPECTION_User_Id") ||
    localStorage.getItem("User Id");

  if (!user) return null;

  const response = await axios.post(
    `${baseUrlNew.baseUrl}api/v1/inspection_cars/`,
    { user }
  );
  const cars = response?.data?.data || [];
  if (!Array.isArray(cars)) return null;
  return (
    cars.find((car) => {
      const cid = car.car_id || car.id;
      return (
        String(cid) === String(carId) ||
        (productId != null && String(toProductId(cid)) === String(productId))
      );
    }) || null
  );
}

function collectSectionArrays(root) {
  const arrays = [];
  const queue = [root];
  const seen = new Set();
  while (queue.length) {
    const node = queue.shift();
    if (!node || typeof node !== "object") continue;
    if (seen.has(node)) continue;
    seen.add(node);
    if (Array.isArray(node)) {
      if (
        node.some(
          (item) =>
            item &&
            typeof item === "object" &&
            (Array.isArray(item.inspection_title) ||
              Array.isArray(item.questions))
        )
      ) {
        arrays.push(node);
      }
      node.forEach((item) => queue.push(item));
      continue;
    }
    Object.values(node).forEach((value) => {
      if (value && typeof value === "object") queue.push(value);
    });
  }
  return arrays;
}

/** Find one checklist section object inside an inspection_cars row. */
export function findChecklistSection(car, sectionId, sectionTitle) {
  if (!car) return null;
  const pools = [
    car.report,
    car.selected_inspection,
    car.inspection,
    car.inspections,
    car.checklist,
    car.questions,
    car.inspection_data,
    car.data,
    car,
  ];

  for (const pool of pools) {
    let sections = pool;
    if (typeof sections === "string") {
      try {
        sections = JSON.parse(sections);
      } catch {
        continue;
      }
    }
    if (sections && !Array.isArray(sections) && typeof sections === "object") {
      if (Array.isArray(sections.data?.data)) sections = sections.data.data;
      else if (Array.isArray(sections.data)) sections = sections.data;
      else if (Array.isArray(sections.report)) sections = sections.report;
      else sections = collectSectionArrays(sections).flat();
    }
    if (!Array.isArray(sections)) continue;
    const match = sections.find(
      (section) =>
        section &&
        (String(section.id) === String(sectionId) ||
          (sectionTitle && section.title === sectionTitle))
    );
    if (match) return match;
  }
  return null;
}

export function buildAnswersFromSelections(questions, divindex) {
  if (!Array.isArray(questions)) return [];
  return questions
    .map((question, index) => {
      const choiceId = divindex?.[index];
      if (choiceId === undefined || choiceId === null || choiceId === "") {
        return null;
      }
      const choice = (question.choices || []).find(
        (item) => String(item.id) === String(choiceId)
      );
      if (!choice) return null;
      return `${question.question}: ${choice.choice}`;
    })
    .filter(Boolean);
}
